import { useState } from 'react';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { StatusText, type StatusTone } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { SURVEY_ACTS as ACTS, type SurveyAct as Act } from '../utils/surveyActs';
import { SURVEY_STATUS, SURVEY_STATUS_LABELS, type Survey, type SurveyStatusValue } from '../surveyTypes';

const TONES: Record<SurveyStatusValue, StatusTone> = { 0: 'pending', 1: 'active', 2: 'inactive' };

/** An instant, or NULL itself — so the table's own em dash shows for a step not taken yet. */
function instant(value?: string | null) {
  return value != null ? (
    <time dateTime={toIso(value)} className="font-mono">
      {formatDateTime(value)}
    </time>
  ) : null;
}

/**
 * Info › Survey status (it sat on Configuration › Settings until 13 Oct 2026) — where the survey is in its run, and the two acts that move it
 * (product call, 12 Oct 2026; the title, divisions and positions it replaced stay on Info).
 *
 * Not started → **Start survey** → Started → **End survey** → Done → **Re-open survey** → Started
 * again. Three acts, each offered only from the status it applies to. The questionnaire is answered only while Started — the server's rule; this card
 * is where it is opened and closed. The acts sit behind the cog on `surveys.update`, are disabled
 * for STATE with the reason, and CONFIRM with the consequence stated, because the record has
 * moved by the time the dialog closes.
 */
export function SurveyStatusCard({ survey, onChanged }: { survey: Survey; onChanged: (next: Survey) => void }) {
  const toast = useToast();
  const [pending, setPending] = useState<Act | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const deleted = survey.isActive ? null : 'This survey is deleted. Restore it before changing its status.';
  const started = survey.status === SURVEY_STATUS.Started;
  const done = survey.status === SURVEY_STATUS.Done;

  async function run() {
    if (!pending) return;

    setRunning(true);
    setError(null);

    try {
      const next = await ACTS[pending].run(survey.id);

      toast.success(ACTS[pending].done);
      setPending(null);
      onChanged(next);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <ProfileCard
        id="survey-profile-status"
        title="Survey status"
        menuItems={[
          {
            key: 'start',
            label: 'Start survey',
            permission: 'surveys.update',
            disabledReason:
              deleted ??
              (started ? 'This survey has already started.' : done ? 'This survey has ended. Re-open it instead.' : null),
            onClick: () => setPending('start'),
          },
          {
            key: 'end',
            label: 'End survey',
            permission: 'surveys.update',
            disabledReason:
              deleted ?? (started ? null : done ? 'This survey has already ended.' : 'This survey has not started yet.'),
            onClick: () => setPending('end'),
          },
          {
            key: 'reopen',
            label: 'Re-open survey',
            permission: 'surveys.update',
            disabledReason: deleted ?? (done ? null : 'Only a survey that has ended can be re-opened.'),
            onClick: () => setPending('reopen'),
          },
        ]}
      >
        <PreviewTable
          variant="table-display"
          rows={[
            {
              label: 'Status',
              value: <StatusText tone={TONES[survey.status]}>{SURVEY_STATUS_LABELS[survey.status]}</StatusText>,
            },
            { label: 'Started', value: instant(survey.startedAt) },
            { label: 'Started by', value: survey.startedByName },
            { label: 'Ended', value: instant(survey.endedAt) },
            { label: 'Ended by', value: survey.endedByName },
          ]}
        />
      </ProfileCard>

      {pending ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setError(null);
          }}
          onConfirm={() => void run()}
          title={ACTS[pending].title}
          requireConfirm
          loading={running}
          id={`survey-${pending}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {ACTS[pending].consequence}
              </p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}
